import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { HttpError, type RowOutcome, type ScheduleChangeResult } from '@voll-intelligence/domain';

import { scheduleChangeRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import { Card, PageSectionHead, useOrgContext } from '@web/ui/core';

import { UploadForm } from '../components/upload_form';
import { ResultTable } from '../components/result_table';

interface SubmitVars {
  processorType: string;
  file: File;
}

/**
 * Operator screen for involuntary schedule-change uploads. Picks a processor
 * type + file, posts to the kanban-api bridge, and renders the per-row parse
 * outcome (summary header + table). A whole-file 400 surfaces as an error
 * banner above an empty table; no row caching.
 */
export function ScheduleChangesPage(): React.JSX.Element {
  // `orgId` is the resolved 24-char hex ObjectId — the backend boundary the
  // kanban-api route validates. The address-bar slug is never sent.
  const { orgId } = useOrgContext();

  const [processorType, setProcessorType] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const upload = useMutation<ScheduleChangeResult, unknown, SubmitVars>({
    mutationFn: (vars) =>
      scheduleChangeRepository.submitUpload(orgId, vars.processorType, vars.file),
  });

  function submit(): void {
    if (file) upload.mutate({ processorType, file });
  }

  let resultSection: React.JSX.Element | null;
  if (upload.isError) {
    resultSection = <ResultTable rows={[]} />;
  } else if (upload.data) {
    resultSection = (
      <>
        <SummaryHeader result={upload.data} />
        <ResultTable rows={upload.data.rows} />
      </>
    );
  } else {
    resultSection = null;
  }

  return (
    <div className="mx-auto flex max-w-prose-2xl flex-col gap-6 p-6">
      <PageSectionHead title={t('scheduleChanges.title')} sub={t('scheduleChanges.sub')} />

      <Card>
        <div className="p-5">
          <UploadForm
            processorType={processorType}
            onProcessorTypeChange={setProcessorType}
            file={file}
            onFileChange={setFile}
            onSubmit={submit}
            isPending={upload.isPending}
          />
        </div>
      </Card>

      {upload.isError ? <ErrorBanner error={upload.error} /> : null}

      {resultSection}
    </div>
  );
}

/** Reads the whole-file `reason` from the thrown `HttpError` details. */
function ErrorBanner({ error }: Readonly<{ error: unknown }>): React.JSX.Element {
  let message: string;
  if (error instanceof HttpError && typeof error.details?.reason === 'string') {
    message = error.details.reason;
  } else if (error instanceof Error) {
    message = error.message;
  } else {
    message = String(error);
  }
  return (
    <div
      role="alert"
      className="rounded-md border border-tone-red-fg/30 bg-tone-red-tint px-4 py-3 text-meta text-tone-red-ink"
    >
      {message}
    </div>
  );
}

function countByOutcome(rows: RowOutcome[], outcome: RowOutcome['outcome']): number {
  return rows.filter((r) => r.outcome === outcome).length;
}

/** Total + per-outcome counts (only non-zero buckets are shown). */
function SummaryHeader({ result }: Readonly<{ result: ScheduleChangeResult }>): React.JSX.Element {
  const { totalRows, rows } = result;
  const parts: string[] = [t('scheduleChanges.summary.rows', { count: totalRows })];

  const matched = countByOutcome(rows, 'matched');
  const voucherNotFound = countByOutcome(rows, 'voucher-not-found');
  const travelerUnbound = countByOutcome(rows, 'traveler-unbound');
  const ambiguousLocator = countByOutcome(rows, 'ambiguous-locator');
  const parseError = countByOutcome(rows, 'parse-error');
  const enrichmentError = countByOutcome(rows, 'enrichment-error');

  if (matched > 0) parts.push(t('scheduleChanges.summary.matched', { count: matched }));
  if (voucherNotFound > 0)
    parts.push(t('scheduleChanges.summary.voucherNotFound', { count: voucherNotFound }));
  if (travelerUnbound > 0)
    parts.push(t('scheduleChanges.summary.travelerUnbound', { count: travelerUnbound }));
  if (ambiguousLocator > 0)
    parts.push(t('scheduleChanges.summary.ambiguousLocator', { count: ambiguousLocator }));
  if (parseError > 0) parts.push(t('scheduleChanges.summary.parseError', { count: parseError }));
  if (enrichmentError > 0)
    parts.push(t('scheduleChanges.summary.enrichmentError', { count: enrichmentError }));

  return <p className="m-0 text-meta font-medium text-foreground">{parts.join(' · ')}</p>;
}
